import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, Image, Eye, Download, Rocket, Stars, History, Globe, Search, Edit2, Brain, Sparkles, FolderOpen, Bot, Code, ChevronUp, Settings, CheckCircle, Bug } from 'lucide-react';
import MCPSelector from './MCPSelector';
import { useMCP } from '../contexts/MCPContext';
import { SeasonalCornerDecoration } from './seasonal/SeasonalDecorations';

const WelcomeSplash = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showMCPSelector, setShowMCPSelector] = useState(false);
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const { mcpServers, selectedMCPs } = useMCP();

  useEffect(() => {
    // Fade in effect
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      // Set a flag in localStorage so it doesn't show again
      localStorage.setItem('v220WelcomeSeen', 'true');
    }, 300);
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0'
    }`}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className={`relative max-w-xl w-full max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 ${
        isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
      }`}>
        {/* Seasonal Corner Decorations */}
        <SeasonalCornerDecoration position="top-left" />
        <SeasonalCornerDecoration position="top-right" />

        {/* Header - Minimal gradient */}
        <div className="relative bg-gradient-to-r from-gray-900 to-gray-800 p-5 text-white">

          <div className="absolute top-4 right-4 flex gap-2 z-20">
            <div className="relative">
              <button
                onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
                className="p-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors cursor-pointer"
                title="Options"
              >
                <Settings className="w-5 h-5" />
              </button>

              {showOptionsDropdown && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-30">
                  <div className="p-3">
                    <button
                      onClick={() => {
                        setShowMCPSelector(true);
                        setShowOptionsDropdown(false);
                      }}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Code className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-900">MCP Servers</span>
                      </div>
                      {selectedMCPs.length > 0 && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {selectedMCPs.length}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-600 rounded-lg">
              <CheckCircle className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Updated to v2.2.0 🎫</h1>
              <p className="text-sm opacity-75">Black Friday Scratch & Win + Pagination</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {showChangelog ? (
            // Full Changelog view
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <History className="w-5 h-5 text-purple-600" />
                  Full Changelog
                </h3>
                <button
                  onClick={() => setShowChangelog(false)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  ← Back
                </button>
              </div>

              <div className="space-y-2">
                <div className="p-3 bg-gradient-to-r from-black to-yellow-600 rounded-lg border border-yellow-400">
                  <h4 className="font-medium text-white text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    v2.2.0 - Black Friday Scratch & Win 🎫 (Current)
                  </h4>
                  <p className="text-xs text-yellow-100 mt-1">Daily scratch card game (Nov 20-Dec 1) with generous rewards! Win 5,000-100,000 free credits. Plus conversation pagination with "Load Previous Conversations" button for better performance.</p>
                </div>

                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-medium text-orange-900 text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    v2.0.8 - Seasonal Themes & Halloween 🎃
                  </h4>
                  <p className="text-xs text-orange-700 mt-1">Festive seasonal themes system! Enjoy spooky Halloween decorations with floating ghosts, flying bats, and seasonal animations. Automatically activates for Halloween, Melbourne Cup, Christmas, and New Year.</p>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                    <Search className="w-4 h-4" />
                    v2.0.7 - Google Ads Keyword Planner MCP
                  </h4>
                  <p className="text-xs text-gray-700 mt-1">Complete MCP integration for Google Ads Keyword Planner API. Research keywords, get search volumes, competition data, and location/language targeting directly through Claude.</p>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    v2.0.6 - SEranking Agent Tool
                  </h4>
                  <p className="text-xs text-gray-700 mt-1">Added natural language SEO rankings query tool powered by SEranking API. Ask Claude about keyword rankings, traffic, ads data, and SEO performance in plain English.</p>
                </div>

                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-medium text-yellow-900 text-sm flex items-center gap-2">
                    <Bug className="w-4 h-4" />
                    v2.0.5 - Bug Fixes
                  </h4>
                  <ul className="text-xs text-yellow-800 mt-2 space-y-1 list-disc list-inside">
                    <li>Fixed MCP tools not executing (showing "Starting tool execution" forever)</li>
                    <li>Fixed web search results not rendering after page refresh</li>
                    <li>Fixed 1M context toggle not being sent to backend</li>
                    <li>Added 1M context toggle to chat interface Options dropdown</li>
                  </ul>
                </div>

                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-medium text-green-900 text-sm">v2.0.4 - MCP Test Connection</h4>
                  <p className="text-xs text-green-700 mt-1">Added test connection button for each MCP server in settings. Validate credentials and see available tools before using MCP in chat.</p>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 text-sm">v2.0.3 - DataForSEO MCP Integration</h4>
                  <p className="text-xs text-blue-700 mt-1">Comprehensive SEO research and analysis capabilities with 9 API modules including SERP, Keywords Data, OnPage, Backlinks, and more.</p>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 text-sm">v2.0.2 - UI Fixes</h4>
                  <p className="text-xs text-blue-700 mt-1">Fixed auto mode messages disappearing and restored Options dropdown menu with full functionality.</p>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 text-sm">v2.0.1 - Dark Mode & Google Ads Fixes</h4>
                  <p className="text-xs text-blue-700 mt-1">Enhanced dark mode visibility and fixed Google Ads MCP permission errors.</p>
                </div>

                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-medium text-purple-900 text-sm">v2.0.0 - MCP Integration</h4>
                  <p className="text-xs text-purple-700 mt-1">Major update: Model Context Protocol (MCP) integration for connecting external tools and data sources like Google Ads, databases, and file systems.</p>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 text-sm">v1.6.x - Claude Sonnet 4.5 & Performance</h4>
                  <p className="text-xs text-gray-600 mt-1">Added Claude Sonnet 4.5 support, improved textarea expansion, eliminated input lag, and enhanced welcome screen.</p>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 text-sm">v1.5.x - Context Spaces & Advanced Features</h4>
                  <p className="text-xs text-gray-600 mt-1">Resource upload, AI Apps, GPT-5 support with Ph.D.-level reasoning, Memory feature, search conversations, and credit system.</p>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 text-sm">v1.4.x & v1.3.x - Multi-Model & Image Generation</h4>
                  <p className="text-xs text-gray-600 mt-1">Added Google Gemini and Grok AI, file upload & vision support, DALL-E 3 image generation, and enhanced web search.</p>
                </div>
              </div>
            </div>
          ) : (
            // Minimal "What's New" view - Black Friday Theme
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-gray-900 mb-1">What's New 🎫</h2>
                <p className="text-sm text-gray-600">Version 2.1.0 - Black Friday Bonanza!</p>
              </div>

              {/* New Feature - Simple and focused */}
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-black to-yellow-100 rounded-lg border-2 border-yellow-400">
                  <Sparkles className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Black Friday Scratch & Win 🎫</p>
                    <p className="text-xs text-gray-600 mt-0.5">Daily scratch card game (Nov 20 - Dec 1)! Win generous credits: 5,000-10,000 common, 25,000 rare, 50,000 epic, or 100,000 legendary! Credits auto-added to your account.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Bot className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Load Previous Conversations</p>
                    <p className="text-xs text-gray-600 mt-0.5">New manual load button for conversation pagination! Click "Load Previous Conversations" at the bottom of the sidebar to fetch older chats in batches of 50. Better performance for large histories.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Stars className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Dismiss Options</p>
                    <p className="text-xs text-gray-600 mt-0.5">Black Friday badge includes dismiss options - hide for today or permanently. Clean UI that respects your preferences!</p>
                  </div>
                </div>
              </div>

              {/* View full changelog link */}
              <div className="text-center pt-2 border-t">
                <button
                  onClick={() => setShowChangelog(true)}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors inline-flex items-center gap-1"
                >
                  <History className="w-4 h-4" />
                  View full changelog
                </button>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="pt-2">
            <button
              onClick={handleClose}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
            >
              <Brain className="w-5 h-5" />
              Start Chatting
              <Sparkles className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* MCP Selector Modal */}
      {showMCPSelector && (
        <MCPSelector onClose={() => setShowMCPSelector(false)} />
      )}
    </div>
  );
};

export default WelcomeSplash;
